'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/features/auth';
import type { ContainerType } from '../types';

interface ContainerTypeCardProps {
  item: ContainerType;
  onEdit: (item: ContainerType) => void;
  onDelete: (item: ContainerType) => void;
}

export function ContainerTypeCard({
  item,
  onEdit,
  onDelete
}: ContainerTypeCardProps) {
  const t = useTranslations('containerTypes');

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        <div className='mb-3 flex items-start justify-between'>
          <h3 className='font-semibold'>{item.name}</h3>
          <Badge
            variant={item.status === 'active' ? 'default' : 'secondary'}
            className={cn(
              item.status === 'active'
                ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
                : 'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200'
            )}
          >
            {item.status === 'active' ? t('statusActive') : t('statusInactive')}
          </Badge>
        </div>

        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <PermissionGate permissions={['container_types.edit']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onEdit(item)}
            >
              <Pencil className='h-4 w-4' />
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['container_types.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onDelete(item)}
            >
              <Trash2 className='text-destructive h-4 w-4' />
            </Button>
          </PermissionGate>
        </div>
      </CardContent>
    </Card>
  );
}
